import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Chart, registerables } from 'chart.js';
import { GraphicReportsService } from '../../graphic-reports.service';
import { SharedService } from '@myproject/shared/shared.service';
import { DatePipe } from '@angular/common';
import * as FileSaver from 'file-saver';
import { Title } from '@angular/platform-browser';
import { environment } from '@myenv/environment';

@Component({
    selector: 'app-version-drilldown-report',
    templateUrl: './version-drilldown-report.component.html',
    styleUrls: ['./version-drilldown-report.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VersionDrilldownReportComponent implements OnInit {
  page = 0;
  isLoading = false;
  startTime: number;
  endTime: number;
  firmwares: any[] = [];
  configs: any[] = [];
  fwChart: any;
  configChart: any;
  areaChart: any;
  devices: any[] = [];
  fwSelected: any = {};
  cfgSelected: any = {};
  backgroundColors: any[] = [];
  resetOffset = false;
  hideCalendar = false;
  blockCall = false;
  highestCount = 0;
  searchModel = {
    fwSearchVal: '',
    fwSearchedData: [],
    cfgSearchVal: '',
    cfgSearchedData: [],
  };

  constructor(
    private reportsService: GraphicReportsService,
    private title: Title,
    private sharedService: SharedService
  ) { }

  ngOnInit() {
    Chart.register(...registerables);
    this.title.setTitle('Software Version' + environment.title_text);
  }

  onTimeChange(e) {
    this.startTime = e.startTime;
    this.endTime = e.endTime;
    this.page = 0;
    this.resetOffset = !this.resetOffset;
      this.searchModel = {
        fwSearchVal: '',
        fwSearchedData: [],
        cfgSearchVal: '',
        cfgSearchedData: [],
      };
    this.getFirmwareCount(e.startTime, e.endTime);
  }

  onPageChange(page) {
    this.page = 0;
    this.getFwConfigDevices(this.startTime, this.endTime, this.fwSelected.firmwareVersion, this.cfgSelected.configVersion, page);
  }

  refresh() {
    this.page = 0;
    this.resetOffset = !this.resetOffset;
    this.searchModel = {
      fwSearchVal: '',
      fwSearchedData: [],
      cfgSearchVal: '',
      cfgSearchedData: [],
    };
    this.getFirmwareCount(this.startTime, this.endTime);
  }

  onFWSearch() {
    const global = this;
    this.searchModel.fwSearchedData = this.firmwares.filter(function (item) {
      return item.firmwareVersion.toLowerCase().includes(global.searchModel.fwSearchVal.toLowerCase());
    });
  }

  onCFGSearch() {
    const global = this;
    this.searchModel.cfgSearchedData = this.configs.filter(function (item) {
      return item.configVersion.toLowerCase().includes(global.searchModel.cfgSearchVal.toLowerCase());
    });
  }

  setBackgroundColors(res) {
    res.map(item => {
      const r = Math.floor(Math.random() * 254);
      const g = Math.floor(Math.random() * 254);
      const b = Math.floor(Math.random() * 254);
      this.backgroundColors.push('rgb(' + r + ',' + g + ',' + b + ')');
    });
  }

  getFirmwareCount(startTime, endTime) {
    this.isLoading = true;
    this.reportsService.getFirmwareCount(startTime, endTime).subscribe({
      next: res => {
        if (res.length) {
          // console.log(res);
          this.firmwares = res;
          this.searchModel.fwSearchedData = res;
          this.fwSelected = res[0];
          this.setBackgroundColors(res);
          this.drawPieChart('pieChartFW', res);
          this.getConfigCount(startTime, endTime, res[0].firmwareVersion);
        } else {
          this.firmwares = [];
          this.searchModel.fwSearchedData = [];
          this.configs = [];
          this.devices = [];
          this.fwSelected = {};
          this.cfgSelected = {};
          this.drawPieChart('pieChartFW', []);
          this.drawPieChart('pieChartCFG', []);
          this.drawAreaGraph([]);
          this.isLoading = false;
        }
      },
      error: error => {
        this.isLoading = false;
        this.sharedService.getErrorMsg(error);
      }
    });
  }

  getConfigCount(startTime, endTime, firmware) {
    this.reportsService.getConfigCount(startTime, endTime, firmware).subscribe({
      next: res => {
        if (res.length) {
          // console.log(res);
          this.configs = res;
          this.searchModel.cfgSearchedData = res;
          this.cfgSelected = res[0];
          this.drawPieChart('pieChartCFG', res);
          this.getFwConfigTrendData(startTime, endTime, firmware, res[0].configVersion);
          this.getFwConfigDevices(startTime, endTime, firmware, res[0].configVersion, this.page);
        } else {
          this.configs = [];
          this.searchModel.cfgSearchedData = [];
          this.devices = [];
          this.cfgSelected = {};
          this.drawPieChart('pieChartCFG', []);
          this.drawAreaGraph([]);
          this.isLoading = false;
        }
      },
      error: error => {
        this.isLoading = false;
        this.sharedService.getErrorMsg(error);
      }
    });
  }

  getFwConfigTrendData(startTime, endTime, firmware, config) {
    this.reportsService.getFwConfigTrendData(startTime, endTime, firmware, config).subscribe({
      next: res => {
        if (res.length) {
          if (this.areaChart) {
            this.areaChart.destroy();
          }
          this.drawAreaGraph(res);
        } else {
          this.drawAreaGraph([]);
        }
        this.isLoading = false;
      },
      error: error => {
        this.isLoading = false;
        this.sharedService.getErrorMsg(error);
      }
    });
  }

  getFwConfigDevices(startTime, endTime, firmware, config, page) {
    this.reportsService.getFwConfigDevices(startTime, endTime, firmware, config, page).subscribe({
      next: res => {
        if (!res.length) { this.blockCall = true; }
        if (page === 0) {
          this.devices = res;
        } else {
          this.devices = [...this.devices, ...res];
        }
      },
      error: error => {
        this.sharedService.getErrorMsg(error);
      }
    });
  }

  downloadFwConfigDevices(attributes) {
    this.reportsService.downloadFwConfigDevices(
      this.startTime, this.endTime, this.fwSelected.firmwareVersion, this.cfgSelected.configVersion, attributes
    ).subscribe({
      next: res => {
        const contentDisposition = res.headers.get('Content-Disposition');
        let filename;
        if (contentDisposition == null) {
          const date = new Date();
          filename = 'Device_' + date + '.zip';
        } else {
          filename = contentDisposition.split(';')[1].split('filename')[1].split('=')[1].trim();
        }
        const blob = new Blob([res.body], {
          type: 'application/zip'
        });
        FileSaver.saveAs(blob, filename);
      },
      error: error => {
        this.sharedService.getErrorMsg(error);
      }
    });
  }

  selectFirmware(e) {
    this.page = 0;
    this.blockCall = false;
    this.resetOffset = !this.resetOffset;
    this.fwSelected = e;
    this.searchModel.cfgSearchVal = '';
    this.getConfigCount(this.startTime, this.endTime, this.fwSelected.firmwareVersion);
  }

  selectConfig(e) {
    this.page = 0;
    this.blockCall = false;
    this.resetOffset = !this.resetOffset;
    this.cfgSelected = e;
    this.getFwConfigTrendData(this.startTime, this.endTime, this.fwSelected.firmwareVersion, this.cfgSelected.configVersion);
    this.getFwConfigDevices(this.startTime, this.endTime, this.fwSelected.firmwareVersion, this.cfgSelected.configVersion, 0);
  }

  drawAreaGraph(data) {
    if (this.areaChart) {
      this.areaChart.destroy();
    }
    this.highestCount = 0;
    const labels = [];
    const counts = [];
    let label = '';
    if (data.length) {
      label = `${data[0].firmwareVersion} > ${data[0].configVersion}`;
    }
    data.map(item => {
      const datePipe = new DatePipe('en-US');
      const eventDate = datePipe.transform(item.day, 'dd MMM');
      labels.push(eventDate);
      counts.push(item.count);
      if (item.count > this.highestCount) {
        this.highestCount = item.count;
      }
    });
    var ctx = document.getElementById('areaGraph') as HTMLCanvasElement;
    this.areaChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [{
          label: label,
          data: counts,
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          borderColor: 'rgba(54, 162, 235, 1)',
        }]
      },
      options: {
        responsive: true,
        plugins: {
          filler: {
            propagate: true
          },
          legend: {
            display: false
          },
        },
        scales: {
          x: {
            display: true,
            beginAtZero: true
          },
          y: {
            display: true,
            suggestedMin: 0,
            suggestedMax: 10,
            ticks: {
              stepSize: this.highestCount > 9 ? null : 1
            }
          }
        }
      }
    });
  }

  drawPieChart(container, data) {
    if (container === 'pieChartFW' && this.fwChart) {
      this.fwChart.destroy();
    } else if (container === 'pieChartCFG' && this.configChart) {
      this.configChart.destroy();
    }
    const labels = [];
    const counts = [];
    let label = '';
    data.map(item => {
      if (item.firmwareVersion) {
        label = 'firmware';
        labels.push(item.firmwareVersion);
      } else if (item.configVersion) {
        label = 'config';
        labels.push(item.configVersion);
      }
      counts.push(item.count);
    });
    var ctx = document.getElementById(container) as HTMLCanvasElement;
    var myChart = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: labels,
        datasets: [{
          data: counts,
          backgroundColor: this.backgroundColors,
          borderColor: this.backgroundColors
        }]
      },
      options: {
        responsive: true,
        plugins: {
        legend: {
          position: 'bottom',
          display: false
        },
        },
        scales: {
          x: {
            display: false,
            beginAtZero: true
          },
          y: {
            display: false,
            beginAtZero: true
          }
        }
      }
    });
    if (label === 'firmware') {
      this.fwChart = myChart;
    } else {
      this.configChart = myChart;
    }
  }
}
